// 34개 설비 종류 상수 정의 (ICT 정보통신설비 전용)
// 각 설비의 key와 한국어 title을 관리합니다.
// 주의: 이 파일은 클라이언트/서버 모두에서 사용 가능한 순수 상수만 포함
// 참고: fanCoilUnit(팬코일유닛)은 기계설비법 17조 해당 기계설비로 ICT에서 제외

export const MACHINE_KEYS = [
  // 통신 설비 (COMMUNICATION) - 8개
  "cableInstallation", // 케이블설비
  "pipeInstallation", // 배관설비
  "mainLineInstallation", // 국선인입설비
  "terminalBoxInstallation", // 단자함설비
  "mobileLineInstallation", // 이동통신구내선로설비
  "phoneInstallation", // 전화설비
  "broadcastAntennaSystem", // 방송 공동수신 안테나 시설
  "cableTransmissionInstallation", // 종합유선방송 구내전송선로설비

  // 방송 설비 (BROADCAST) - 1개
  "audioBroadcastInstallation", // 방송음향설비

  // 정보 설비 (INFORMATION) - 23개
  "networkInstallation", // 네트워크설비
  "accessControlSystem", // 전자출입(통제)시스템
  "remoteMeteringSystem", // 원격검침시스템
  "parkingControlSystem", // 주차관제시스템
  "parkingGuidanceSystem", // 주차유도시스템
  "unmannedDeliverySystem", // 무인택배시스템
  "emergencyBellInstallation", // 비상벨설비
  "CCTVSystem", // 영상정보처리기기 시스템
  "homeNetworkUserInstallation", // 홈네트워크 이용자 설비(전유부분)
  "buildingInformationSystem", // 빌딩안내시스템(BIS)
  "electricClockSystem", // 전기시계시스템
  "integratedSISystem", // 통합 SI시스템
  "facilityManagementSystem", // 시설관리시스템(FMS)
  "BEMS", // 건물에너지관리시스템(BEMS)
  "intelligentPeopleCountingSystem", // 지능형 인원계수 시스템
  "intelligentPerimeterSurveillanceSystem", // 지능형 경계 감시 시스템
  "smartHospitalSystem", // 스마트 병원 설비(의료용 너스콜)
  "SmartFactorySystem", // 스마트 공장 시스템
  "SmartAntiTheftSystem", // 스마트 도난방지 시스템
  "SmartLibrarySystem", // 스마트 도서관 시스템
  "IntelligentSoundAnomalySystem", // 지능형 이상음원 시스템
  "IoTBasedUndergroundSafetyManagementSystem", // IoT기반 지하공간 안전관리 시스템
  "DigitalSignage", // 디지털 사이니지

  // 기타 설비 (OTHER) - 2개
  "telecomPowerInstallation", // 통신용 전원설비
  "telecomGroundingInstallation", // 통신접지설비
] as const;

// 설비 key 타입
export type MachineKey = (typeof MACHINE_KEYS)[number];

// key별 한국어 title 매핑 (원본 SummaryReport.js 기준)
export const MACHINE_TITLES: Record<MachineKey, string> = {
  // 통신 설비 (COMMUNICATION) - 8개
  cableInstallation: "케이블설비",
  pipeInstallation: "배관설비",
  mainLineInstallation: "국선인입설비",
  terminalBoxInstallation: "단자함설비",
  mobileLineInstallation: "이동통신구내선로설비",
  phoneInstallation: "전화설비",
  broadcastAntennaSystem: "방송 공동수신 안테나 시설",
  cableTransmissionInstallation: "종합유선방송 구내전송선로설비",

  // 방송 설비 (BROADCAST) - 1개
  audioBroadcastInstallation: "방송음향설비",

  // 정보 설비 (INFORMATION) - 23개
  networkInstallation: "네트워크설비",
  accessControlSystem: "전자출입(통제)시스템",
  remoteMeteringSystem: "원격검침시스템",
  parkingControlSystem: "주차관제시스템",
  parkingGuidanceSystem: "주차유도시스템",
  unmannedDeliverySystem: "무인택배시스템",
  emergencyBellInstallation: "비상벨설비",
  CCTVSystem: "영상정보처리기기 시스템",
  homeNetworkUserInstallation: "홈네트워크 이용자 설비(전유부분)",
  buildingInformationSystem: "빌딩안내시스템(BIS)",
  electricClockSystem: "전기시계시스템",
  integratedSISystem: "통합 SI시스템",
  facilityManagementSystem: "시설관리시스템(Facility Management System)",
  BEMS: "건물에너지관리시스템(BEMS)",
  intelligentPeopleCountingSystem: "지능형 인원계수 시스템",
  intelligentPerimeterSurveillanceSystem: "지능형 경계 감시 시스템",
  smartHospitalSystem: "스마트 병원 설비(의료용 너스콜)",
  SmartFactorySystem: "스마트 공장 시스템",
  SmartAntiTheftSystem: "스마트 도난방지 시스템",
  SmartLibrarySystem: "스마트 도서관 시스템",
  IntelligentSoundAnomalySystem: "지능형 이상음원 시스템",
  IoTBasedUndergroundSafetyManagementSystem: "IoT기반 지하공간 안전관리 시스템",
  DigitalSignage: "디지털 사이니지",

  // 기타 설비 (OTHER) - 2개
  telecomPowerInstallation: "통신용 전원설비",
  telecomGroundingInstallation: "통신접지설비",
};

// 설비 key 유효성 검사
export function isValidMachineKey(key: string): key is MachineKey {
  return MACHINE_KEYS.includes(key as MachineKey);
}

// 설비 카테고리 (순수 TypeScript enum - type-graphql 의존성 없음)
export enum EquipmentCategory {
  COMMUNICATION = "COMMUNICATION",
  BROADCAST = "BROADCAST",
  INFORMATION = "INFORMATION",
  OTHER = "OTHER",
}

// 설비 메타데이터 인터페이스
export interface MachineMeta {
  ratio: number; // 기본 점검 비율 (0.0 ~ 1.0)
  unit: string; // 단위 (식, 대, 개소 등)
  category: EquipmentCategory; // 카테고리
}

// 설비별 메타데이터 (점검비율, 단위, 카테고리)
// 원본 ict-web 프로젝트 기준 분류
export const MACHINE_META: Record<MachineKey, MachineMeta> = {
  // ============================================
  // 통신 설비 (COMMUNICATION) - 8개
  // ============================================
  cableInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.COMMUNICATION },
  pipeInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.COMMUNICATION },
  mainLineInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.COMMUNICATION },
  terminalBoxInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.COMMUNICATION },
  mobileLineInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.COMMUNICATION },
  phoneInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.COMMUNICATION },
  broadcastAntennaSystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.COMMUNICATION },
  cableTransmissionInstallation: {
    ratio: 1.0,
    unit: "식",
    category: EquipmentCategory.COMMUNICATION,
  },

  // ============================================
  // 방송 설비 (BROADCAST) - 1개
  // ============================================
  audioBroadcastInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.BROADCAST },

  // ============================================
  // 정보 설비 (INFORMATION) - 23개
  // ============================================
  networkInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  accessControlSystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  remoteMeteringSystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  parkingControlSystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  parkingGuidanceSystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  unmannedDeliverySystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  emergencyBellInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  CCTVSystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  homeNetworkUserInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  buildingInformationSystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  electricClockSystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  integratedSISystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  facilityManagementSystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  BEMS: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  intelligentPeopleCountingSystem: {
    ratio: 1.0,
    unit: "식",
    category: EquipmentCategory.INFORMATION,
  },
  intelligentPerimeterSurveillanceSystem: {
    ratio: 1.0,
    unit: "식",
    category: EquipmentCategory.INFORMATION,
  },
  smartHospitalSystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  SmartFactorySystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  SmartAntiTheftSystem: { ratio: 1.0, unit: "식", category: EquipmentCategory.INFORMATION },
  SmartLibrarySystem: { ratio: 1.0, unit: "개소", category: EquipmentCategory.INFORMATION },
  IntelligentSoundAnomalySystem: {
    ratio: 1.0,
    unit: "개소",
    category: EquipmentCategory.INFORMATION,
  },
  IoTBasedUndergroundSafetyManagementSystem: {
    ratio: 1.0,
    unit: "개소",
    category: EquipmentCategory.INFORMATION,
  },
  DigitalSignage: { ratio: 1.0, unit: "개소", category: EquipmentCategory.INFORMATION },

  // ============================================
  // 기타 설비 (OTHER) - 2개
  // ============================================
  telecomPowerInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.OTHER },
  telecomGroundingInstallation: { ratio: 1.0, unit: "식", category: EquipmentCategory.OTHER },
};
